/**
 * IndexedDB Utility for Model Data Storage
 *
 * Purpose: 새로고침 시에도 모델 데이터 유지 (localStorage 5MB 한계 우회)
 * Database: 'ModelDataDB'
 * Store: 'models' with keyPath 'id'
 *
 * Storage Structure:
 * {
 *   id: 'current-session',     // 고정 키
 *   multiModelData: Array,     // 열려있는 모든 모델
 *   currentModelKey: string,   // 현재 활성 모델 key
 *   timestamp: number          // 마지막 업데이트 시간
 * }
 *
 * Capacity: IndexedDB는 50MB+ 저장 가능 (localStorage 5MB의 10배 이상)
 */

import { SCHEMA_VERSION_KEY, CURRENT_SCHEMA_VERSION, isDisposableRecordUsable, readSchemaVersion } from 'services/modelSchema';

import { useDebouncedCallback } from 'use-debounce';

class ModelDataDB {
  constructor() {
    this.db = null;
    this.DB_NAME = 'ModelDataDB';
    this.DB_VERSION = 1;
    this.STORE_NAME = 'models';
    this.SESSION_KEY = 'current-session';
    this.initFailed = false;
  }

  /**
   * Initialize IndexedDB connection
   * @returns {Promise<IDBDatabase|null>}
   */
  async init() {
    if (this.initFailed) return null;
    if (this.db) return this.db;

    if (typeof indexedDB === 'undefined') {
      console.warn('[ModelDataDB] IndexedDB not supported');
      this.initFailed = true;
      return null;
    }

    return new Promise((resolve) => {
      try {
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        request.onerror = () => {
          console.error('[ModelDataDB] IndexedDB initialization failed:', request.error);
          this.initFailed = true;
          resolve(null);
        };

        request.onsuccess = () => {
          this.db = request.result;
          resolve(this.db);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(this.STORE_NAME)) {
            db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
          }
        };

        // 타임아웃 (5초)
        setTimeout(() => {
          if (!this.db && !this.initFailed) {
            console.warn('[ModelDataDB] IndexedDB initialization timeout');
            this.initFailed = true;
            resolve(null);
          }
        }, 5000);
      } catch (error) {
        console.error('[ModelDataDB] IndexedDB init exception:', error);
        this.initFailed = true;
        resolve(null);
      }
    });
  }

  /**
   * 모델 데이터를 경량화 (렌더링 속성 제거)
   * @param {Object} model - 모델 객체
   * @returns {Object} 경량화된 모델
   */
  lightenModel(model) {
    if (!model) return model;

    const lightModel = { ...model };

    // 노드 경량화
    if (lightModel.nodes && Array.isArray(lightModel.nodes)) {
      lightModel.nodes = lightModel.nodes.map(node => {
        const lightNode = { ...node };
        // 렌더링 관련 속성 제거
        delete lightNode.measured;
        delete lightNode.selected;
        delete lightNode.dragging;
        delete lightNode.width;
        delete lightNode.height;
        delete lightNode.style;
        delete lightNode.sourcePosition;
        delete lightNode.targetPosition;
        delete lightNode.zIndex;
        delete lightNode.draggable;
        delete lightNode.selectable;
        delete lightNode.connectable;
        delete lightNode.deletable;
        delete lightNode.focusable;
        return lightNode;
      });
    }

    // 엣지 경량화
    if (lightModel.edges && Array.isArray(lightModel.edges)) {
      lightModel.edges = lightModel.edges.map(edge => {
        const lightEdge = { ...edge };
        delete lightEdge.selected;
        delete lightEdge.animated;
        delete lightEdge.style;
        delete lightEdge.zIndex;
        return lightEdge;
      });
    }

    // 임시 데이터 제거
    delete lightModel._cached;
    delete lightModel._temp;
    delete lightModel.analysisCache;

    return lightModel;
  }

  /**
   * 모델 데이터 저장 (multiModelData 전체)
   * @param {Array|Object} multiModelData - 모든 열린 모델 (배열 또는 Record<string, ModelInfo> 객체)
   * @param {string} currentModelKey - 현재 활성 모델의 key
   * @returns {Promise<void>}
   */
  async saveModels(multiModelData, currentModelKey) {
    // multiModelData가 객체(Record)인 경우 배열로 변환
    const modelsArray = Array.isArray(multiModelData)
      ? multiModelData
      : Object.values(multiModelData || {});

    if (!modelsArray || modelsArray.length === 0) {
      return;
    }

    try {
      const db = await this.init();
      if (!db) {
        console.warn('[ModelDataDB] saveModels: IndexedDB unavailable');
        return;
      }

      // 모델 데이터 경량화
      const lightModels = modelsArray.map(model => this.lightenModel(model));

      const data = {
        id: this.SESSION_KEY,
        // 세션 기록에도 스키마 버전을 남긴다. 다만 이 기록은 **변환하지 않는다** —
        // 임시 상태이고, 버전이 다른 기록을 되살리는 편이 더 위험하다. 불러올 때 버리고
        // 클라우드/파일에서 다시 열게 한다.
        [SCHEMA_VERSION_KEY]: CURRENT_SCHEMA_VERSION,
        multiModelData: lightModels,
        currentModelKey: currentModelKey || null,
        timestamp: Date.now()
      };

      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.put(data);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error('[ModelDataDB] Failed to save models:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('[ModelDataDB] saveModels error:', error);
    }
  }

  /**
   * 저장된 모델 데이터 로드
   * @returns {Promise<{multiModelData: Array, currentModelKey: string}|null>}
   */
  async loadModels() {
    try {
      const db = await this.init();
      if (!db) {
        console.warn('[ModelDataDB] loadModels: IndexedDB unavailable');
        return null;
      }

      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve) => {
        const request = store.get(this.SESSION_KEY);

        request.onsuccess = () => {
          const result = request.result;
          // 스키마 버전이 지금과 다르면 쓰지 않는다(옛 기록·새 기록 모두).
          if (result && !isDisposableRecordUsable(result)) {
            console.warn(
              `[ModelDataDB] 세션 기록의 스키마 버전이 현재와 달라 복원하지 않음 ` +
              `(기록 v${readSchemaVersion(result)}, 현재 v${CURRENT_SCHEMA_VERSION})`
            );
            resolve(null);
            return;
          }
          // multiModelData 는 배열/Record 두 모양 모두 들어올 수 있다
          const restoredCount = Array.isArray(result?.multiModelData)
            ? result.multiModelData.length
            : Object.keys(result?.multiModelData || {}).length;
          if (result && restoredCount > 0) {
            resolve({
              multiModelData: result.multiModelData,
              currentModelKey: result.currentModelKey
            });
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error('[ModelDataDB] Failed to load models:', request.error);
          resolve(null);
        };
      });
    } catch (error) {
      console.error('[ModelDataDB] loadModels error:', error);
      return null;
    }
  }

  /**
   * 저장된 모델 데이터 삭제
   * @returns {Promise<void>}
   */
  async clearModels() {
    try {
      const db = await this.init();
      if (!db) return;

      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve) => {
        const request = store.delete(this.SESSION_KEY);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error('[ModelDataDB] Failed to clear models:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.error('[ModelDataDB] clearModels error:', error);
    }
  }

  /**
   * 저장소 사용량 통계
   * @returns {Promise<Object>}
   */
  async getStorageStats() {
    try {
      const result = await this.loadModels();
      if (!result) {
        return { modelCount: 0, estimatedSizeKB: 0 };
      }

      const size = JSON.stringify(result.multiModelData).length / 1024;
      return {
        modelCount: result.multiModelData.length,
        estimatedSizeKB: size.toFixed(2),
        currentModelKey: result.currentModelKey
      };
    } catch (error) {
      console.error('[ModelDataDB] getStorageStats error:', error);
      return null;
    }
  }

  /**
   * 데이터베이스 연결 종료
   */
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// Singleton instance
export const modelDataDB = new ModelDataDB();

// Export class for testing
export { ModelDataDB };
